import React, { useState, useEffect, useRef } from 'react';
import { Card, Button, Table, Statistic, Row, Col, message, Tag, Space, Alert, Typography, Badge } from 'antd';
import { FolderOpenOutlined, CheckCircleOutlined, PlayCircleOutlined, ReloadOutlined, LoadingOutlined, SyncOutlined } from '@ant-design/icons';
import { getBatchStats, getBatchStatus, runBatchImport } from '../services/api';

const { Title, Text } = Typography;

export default function BatchImportPage() {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(false);
    const [running, setRunning] = useState(false);
    const [starting, setStarting] = useState(false);
    const pollRef = useRef(null);

    const fetchStats = async () => {
        setLoading(true);
        try {
            const res = await getBatchStats();
            setStats(res.data.data);
        } catch (err) {
            message.error('Failed to fetch batch stats');
        } finally {
            setLoading(false);
        }
    };

    const checkStatus = async () => {
        try {
            const res = await getBatchStatus();
            const wasRunning = running;
            setRunning(res.data.isRunning);
            if (wasRunning && !res.data.isRunning) {
                // Just finished — refresh stats and notify
                fetchStats();
                message.success('Batch processing completed!');
            } else if (res.data.isRunning) {
                fetchStats();
            }
        } catch (_) {}
    };

    // Start/stop polling based on running state
    useEffect(() => {
        if (running) {
            pollRef.current = setInterval(checkStatus, 3000);
        } else {
            clearInterval(pollRef.current);
        }
        return () => clearInterval(pollRef.current);
    }, [running]);

    // Initial load + slow poll for stats when idle
    useEffect(() => {
        fetchStats();
        checkStatus();
        const idlePoll = setInterval(() => {
            if (!running) fetchStats();
        }, 15000);
        return () => clearInterval(idlePoll);
    }, []);

    const handleRunBatch = async () => {
        setStarting(true);
        try {
            await runBatchImport();
            setRunning(true);
            message.info('Batch processing started — updating status every 3 seconds');
            fetchStats();
        } catch (err) {
            message.error(err.response?.data?.error || 'Failed to start batch process');
        } finally {
            setStarting(false);
        }
    };

    const historyColumns = [
        {
            title: 'Time',
            dataIndex: 'started_at',
            render: (t) => new Date(t).toLocaleString('th-TH')
        },
        { title: 'Filename', dataIndex: 'filename' },
        { title: 'Imported', dataIndex: 'imported_events' },
        {
            title: 'Status',
            dataIndex: 'status',
            render: (s) => {
                if (s === 'processing') {
                    return <Tag icon={<SyncOutlined spin />} color="processing">PROCESSING</Tag>;
                }
                return (
                    <Tag color={s === 'completed' ? 'green' : s === 'failed' ? 'red' : 'orange'}>
                        {s.toUpperCase()}
                    </Tag>
                );
            }
        }
    ];

    return (
        <div style={{ padding: '24px' }}>
            <Title level={2}>Batch Log Processing</Title>
            <Text type="secondary">Process files from the staging folder with automatic de-duplication.</Text>

            {running && (
                <Alert
                    message={
                        <Space>
                            <LoadingOutlined spin />
                            <span>กำลังประมวลผลไฟล์... กรุณารอ — หน้านี้จะอัปเดตอัตโนมัติทุก 3 วินาที</span>
                        </Space>
                    }
                    type="warning"
                    showIcon={false}
                    style={{ marginTop: 16 }}
                />
            )}

            <Row gutter={24} style={{ marginTop: 24 }}>
                <Col span={8}>
                    <Card>
                        <Statistic
                            title="Files in Staging"
                            value={stats?.stagingCount || 0}
                            prefix={<FolderOpenOutlined />}
                            valueStyle={{ color: stats?.stagingCount > 0 ? '#1890ff' : '#999' }}
                        />
                    </Card>
                </Col>
                <Col span={8}>
                    <Card>
                        <Statistic
                            title="Processed (Total)"
                            value={stats?.processedCount || 0}
                            prefix={<CheckCircleOutlined />}
                            valueStyle={{ color: '#52c41a' }}
                        />
                    </Card>
                </Col>
                <Col span={8}>
                    <Card style={{ textAlign: 'center' }}>
                        <Button
                            type="primary"
                            size="large"
                            icon={running ? <SyncOutlined spin /> : <PlayCircleOutlined />}
                            onClick={handleRunBatch}
                            loading={starting}
                            disabled={running || stats?.stagingCount === 0}
                            block
                            style={{ height: '64px', fontSize: '18px' }}
                        >
                            {running ? 'Processing...' : 'Process All Pending'}
                        </Button>
                        {running && (
                            <Text type="secondary" style={{ fontSize: 12, marginTop: 8, display: 'block' }}>
                                <Badge status="processing" /> กำลังทำงานอยู่
                            </Text>
                        )}
                    </Card>
                </Col>
            </Row>

            <Alert
                message="Instructions"
                description={
                    <ul>
                        <li>Copy your log files to <code>server/uploads/staging</code> folder.</li>
                        <li>Click "Process All Pending" to start background ingestion.</li>
                        <li>The system will automatically skip duplicate files based on their hash.</li>
                        <li>Files will be moved to <code>server/uploads/processed</code> when finished.</li>
                    </ul>
                }
                type="info"
                showIcon
                style={{ marginTop: 24 }}
            />

            <Card
                title={
                    <Space>
                        Recent Import Activity
                        {running && <Badge status="processing" text="Live" />}
                    </Space>
                }
                style={{ marginTop: 24 }}
                extra={<Button icon={<ReloadOutlined />} onClick={fetchStats} loading={loading}>Refresh</Button>}
            >
                <Table
                    dataSource={stats?.recentImports || []}
                    columns={historyColumns}
                    rowKey="import_id"
                    pagination={false}
                    loading={loading}
                />
            </Card>
        </div>
    );
}
